import { DateType } from "@date-io/type";
import { DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import { DotLegalSelectOption } from "@dotlegal/dotlegal-ui-components/dist/cjs/types/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import DotLegalAutocomplete, { AutocompleteOption } from "../../common/autocomplete/DotLegalAutocomplete";
import DotLegalDatePicker from "../../common/datePicker/DotLegalDatePicker";
import DotLegalSearch from "../../common/search/DotLegalSearch";
import { CaseStatus } from "../worldMap/WorldMap.types";
import useCasePageStyles from "./Case.styles";
import { CaseAutocompleteOptions, CaseFilter, CaseRenewalDateFilter, CaseType } from "./Case.types";
import { getCaseFilterOptions, initialCaseFilter, initialCaseFilterOptions } from "./CaseService";
import { CaseSummarization } from "./CaseSummarizationPage";

const filterWidth = 200;

const initialSelectedFilterOptions: CaseAutocompleteOptions = {
    applicants: [],
    classes: [],
    catchwords: [],
    countries: [],
    types: [],
    renewalStatuses: [],
};

export interface FiltersProps {
    caseSummarization?: CaseSummarization;
    selectedCaseType: CaseType;
    caseFilter: CaseFilter;
    onCaseFilterChanged: (caseFilter: CaseFilter) => void;
    renewalRegistrationActive: boolean;
}

function CaseFilters(props: FiltersProps) {
    const classes = useCasePageStyles();
    const [filtersHasBeenLoaded, setFiltersHasBeenLoaded] = useState(false);
    const [filtersIsPristine, setFiltersIsPristine] = useState(true);
    const [filterOptions, setFilterOptions] = useState(initialCaseFilterOptions);
    const [selectedFilterOption, setSelectedFilterOption] = useState(initialSelectedFilterOptions);
    const [statusFilterOptions, setStatusFilterOptions] = useState<DotLegalSelectOption[]>([]);
    const [selectedStatusFilterOption, setSelectedStatusFilterOption] = useState<string | null>("registered");

    const onDateChange = (date: DateType | null, value: string | null, criteria: keyof CaseRenewalDateFilter): void => {
        const newFilter = { ...props.caseFilter };
        if (criteria === "renewalDateFrom") {
            newFilter.renewalDateFrom = date;
        }
        if (criteria === "renewalDateTo") {
            newFilter.renewalDateTo = date;
        }
        onFilterChanged(newFilter);
    };

    const onAutocompleteChanged =
        <K extends keyof CaseAutocompleteOptions>(criteria: K) =>
        (selectedFilterOptions: CaseAutocompleteOptions[K]) => {
            const newSelectedFilterOptions = { ...selectedFilterOption };
            newSelectedFilterOptions[criteria] = selectedFilterOptions;
            setSelectedFilterOption(newSelectedFilterOptions);
            if (filtersIsPristine) setFiltersIsPristine(false);
            const newFilter = { ...props.caseFilter };
            newFilter[criteria] = selectedFilterOptions
                .filter((selectedOption: AutocompleteOption<any>) => !!selectedOption)
                .map((selectedOption: AutocompleteOption<any>) => selectedOption.value);

            onFilterChanged(newFilter);
        };

    const onSearchTextChanged = (value: string) => {
        const newFilter = { ...props.caseFilter };
        newFilter.searchText = value;
        onFilterChanged(newFilter);
    };

    const onFilterChanged = (filter: CaseFilter) => {
        const newFilter = { ...filter };
        newFilter.isDispute = props.caseSummarization === CaseSummarization.Disputes;
        props.onCaseFilterChanged(newFilter);
        getCaseFilterOptionsByFilter(newFilter).then();
    };

    const generateAutocomplete = (label: string, criteria: keyof CaseAutocompleteOptions) => {
        if (!criteria) return <></>;
        let selected = filterOptions[criteria]?.filter((f) => props.caseFilter[criteria].includes(f.value ?? ""));

        return (
            <DotLegalAutocomplete
                label={label}
                onChange={onAutocompleteChanged(criteria)}
                options={filterOptions[criteria]}
                value={selected}
                width={filterWidth}
            />
        );
    };

    const generateStatusDropdown = () => {
        return (
            <Box
                sx={(theme) => ({
                    "& .MuiSvgIcon-root": {
                        fill: theme.palette.primary.main,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: `${theme.palette.primary.main} !Important`,
                    },
                    "& .MuiAutocomplete-input": { width: "100% !Important" },
                    "& .MuiFormLabel-root": {
                        "&.Mui-focused": {
                            color: `${theme.palette.primary.main} !Important`,
                        },
                        "&.MuiFormLabel-filled": {
                            color: `${theme.palette.primary.main} !Important`,
                        },
                        color: "rgb(112, 112, 112) !important",
                    },
                })}
            >
                <DotLegalSelect
                    label="Status"
                    selectedItem={selectedStatusFilterOption}
                    options={statusFilterOptions}
                    onChange={(x) => {
                        onStatusAutocompleteChanged(x);
                    }}
                    placeholder=""
                    noOptionsLabel={"No options"}
                    width={`${filterWidth}px`}
                    noMargin
                />
            </Box>
        );
    };

    const onStatusAutocompleteChanged = (selected: string | null) => {
        setSelectedStatusFilterOption(selected);
        let newFilter = { ...props.caseFilter };
        newFilter.status = selected as CaseStatus;
        onFilterChanged(newFilter);
    };

    const getCaseFilterOptionsByFilter = async (filter: CaseFilter) => {
        const response = await getCaseFilterOptions(props.selectedCaseType, filter, props.caseSummarization === CaseSummarization.Renewals);
        if (response) {
            setFilterOptions(response);
            const statuses = response.statuses.map((x) => {
                return { name: x.label, id: x.value!.toLowerCase() };
            });
            setStatusFilterOptions(statuses);
            if (selectedStatusFilterOption === null) {
                setSelectedStatusFilterOption("registered");
            }
        }
        return undefined;
    };

    const resetCaseFilter = (): void => {
        const newFilter = {
            ...initialCaseFilter(props.caseSummarization === CaseSummarization.Disputes ? "pending" : "registered", props.caseSummarization),
        };
        newFilter.status = props.caseFilter.status;
        newFilter.renewalDateFrom = props.caseFilter.renewalDateFrom;
        newFilter.renewalDateTo = props.caseFilter.renewalDateTo;
        newFilter.catchwords = props.caseFilter.catchwords;
        newFilter.classes = props.caseFilter.classes;
        newFilter.countries = props.caseFilter.countries;
        newFilter.applicants = props.caseFilter.applicants;
        newFilter.renewalStatuses = props.caseFilter.renewalStatuses;
        setSelectedFilterOption(initialSelectedFilterOptions);
        setSelectedStatusFilterOption(props.caseSummarization === CaseSummarization.Watches ? "registered" : null);
        onFilterChanged(newFilter);
    };

    useEffect(() => {
        if (filtersHasBeenLoaded) getCaseFilterOptionsByFilter(props.caseFilter).then();
        // eslint-disable-next-line
    }, [props.caseFilter.status]);

    useEffect(() => {
        if (!filtersHasBeenLoaded) setFiltersHasBeenLoaded(true);
        getCaseFilterOptionsByFilter(props.caseFilter);
        // eslint-disable-next-line
    }, [props.selectedCaseType]);

    useEffect(() => {
        resetCaseFilter();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <div className={classes.filterContainer}>
                {generateAutocomplete(props.selectedCaseType === CaseType.Watches ? "Watch" : props.selectedCaseType, "catchwords")}
                {(props.caseSummarization === CaseSummarization.Renewals || props.caseSummarization === CaseSummarization.Costs) && (
                    <Box
                        sx={{
                            "& .MuiButtonBase-root-MuiPickersDay-root.Mui-selected": {
                                backgroundColor: "#B8C7FF !important",
                            },
                        }}
                    >
                        <DotLegalDatePicker
                            criteria="renewalDateFrom"
                            label="From"
                            onDateChange={onDateChange}
                            selectedValue={props.caseFilter.renewalDateFrom ?? null}
                        />
                    </Box>
                )}
                {(props.caseSummarization === CaseSummarization.Renewals || props.caseSummarization === CaseSummarization.Costs) && (
                    <DotLegalDatePicker
                        criteria="renewalDateTo"
                        label="To"
                        onDateChange={onDateChange}
                        selectedValue={props.caseFilter.renewalDateTo ?? null}
                    />
                )}
                {props.selectedCaseType !== CaseType.Copyright && generateAutocomplete("Class", "classes")}
                {generateAutocomplete("Country", "countries")}
                {props.caseSummarization !== CaseSummarization.Disputes && generateAutocomplete("Applicant", "applicants")}
                {props.caseSummarization === CaseSummarization.Renewals &&
                    props.renewalRegistrationActive &&
                    generateAutocomplete("Renewal status", "renewalStatuses")}
                {props?.caseSummarization === CaseSummarization.Disputes && generateAutocomplete("Case type", "types")}
                {props.caseSummarization === CaseSummarization.Watches && generateStatusDropdown()}
                <div className={classes.searchContainer}>
                    <DotLegalSearch onSearchTextChanged={onSearchTextChanged} value={props.caseFilter.searchText} />
                </div>
            </div>
        </>
    );
}

export default CaseFilters;
